import { useEffect } from "react";

import { Button } from "@/components/Button/Button";
import { AIHintDisplay } from "@/features/ai-homework/AIHintDisplay";
import type { QuestionAIState } from "@/features/ai-homework/aiHomeworkTypes";
import type { HeadingLevel } from "@/features/content/TextUnit";

import { QuestionContent } from "./QuestionContent";
import type { NormalizedDisclosureData } from "./questionNormalizer";
import styles from "./QuestionInfoPanelContent.module.css";

interface HintTabContentProps {
  aiHint: QuestionAIState["hint"];
  builtInHint?: NormalizedDisclosureData;
  headingLevel?: HeadingLevel;
  onRequestAIHint: () => void;
  onVoteAIHint: (vote: "up" | "down") => void;
  showAIHintButton: boolean;
}

function hasVisibleDisclosure(disclosure?: NormalizedDisclosureData) {
  return Boolean(disclosure?.isVisible && disclosure.units.length > 0);
}

function hasAIHintState(aiHint: QuestionAIState["hint"]) {
  return Boolean(
    aiHint.isLoading ||
    aiHint.isRegenerating ||
    aiHint.error ||
    aiHint.statusMessage ||
    aiHint.response,
  );
}

export function HintTabContent({
  aiHint,
  builtInHint,
  headingLevel = 4,
  onRequestAIHint,
  onVoteAIHint,
  showAIHintButton,
}: HintTabContentProps) {
  const showBuiltInHint = hasVisibleDisclosure(builtInHint);
  const showAIContent = showAIHintButton || hasAIHintState(aiHint);
  const showBuiltInHeading = showBuiltInHint && showAIContent;
  // AI hints are independent of authored hints. This deliberately does NOT check
  // showBuiltInHint: it used to, which meant a question with a visible authored
  // hint never requested its AI hint — and because AIHintDisplay renders nothing
  // while idle, the student saw no AI hint at all even with the setting on.
  const shouldAutoRequestAIHint =
    showAIHintButton &&
    aiHint.response === null &&
    !aiHint.isLoading &&
    !aiHint.isRegenerating &&
    !aiHint.error &&
    !aiHint.statusMessage;
  const showAIHintRetry =
    showAIHintButton &&
    Boolean(aiHint.error) &&
    !aiHint.isLoading &&
    !aiHint.isRegenerating &&
    aiHint.response === null;

  useEffect(() => {
    if (shouldAutoRequestAIHint) {
      void onRequestAIHint();
    }
  }, [onRequestAIHint, shouldAutoRequestAIHint]);

  return (
    <div className={styles.stack}>
      {showBuiltInHint && builtInHint ? (
        <section className={styles.section} aria-label="Built-in hint">
          {showBuiltInHeading ? (
            <h4 className={styles.sectionTitle}>Built-in hint</h4>
          ) : null}
          <QuestionContent
            headingLevel={headingLevel}
            units={builtInHint.units}
          />
        </section>
      ) : null}

      {showAIContent ? (
        <section className={styles.section} aria-label="AI hint controls">
          <AIHintDisplay
            error={aiHint.error}
            isLoading={aiHint.isLoading}
            isRegenerating={aiHint.isRegenerating}
            onDownvote={() => onVoteAIHint("down")}
            onUpvote={() => onVoteAIHint("up")}
            response={aiHint.response}
            statusMessage={aiHint.statusMessage}
            vote={aiHint.vote}
          />
          {showAIHintRetry ? (
            <Button
              className={styles.retryButton}
              onClick={onRequestAIHint}
              size="small"
              variant="secondary"
            >
              Try again
            </Button>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
